import { tapError } from '@core/utils/rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';

import { R } from '@core/utils/r';
import {
  UserCourseResponse,
  UserCourseId,
  UserCourseStepId,
  UserCourseSectionResponse,
  UserCourseStepResponse,
  CourseStepAnswers,
  CourseSessionId,
} from '@business/entities/user_course';
import { map, tap, switchMap } from 'rxjs/operators';
import { CourseSectionId, CourseId } from '@business/entities/course';
import { UserCourseAPI } from '@business/api/userCourse';
import { Observable, of } from 'rxjs';
import { UserId } from '@business/entities/account';

export class UserCourseBloc extends BaseBloc {
  private readonly courseRepo = new Repository<UserCourseResponse>({
    getItemId: course => course.id,
  });

  readonly courses$ = this.courseRepo.items$;
  readonly course$ = this.courseRepo.selectItem;

  readonly step$ = (courseId: UserCourseId, sectionId: CourseSectionId, stepId: UserCourseStepId) => {
    return this.courseRepo.selectItem(courseId).pipe(
      map(course => {
        if (course) {
          const sections = (R.get(course, 'sections') || []) as UserCourseSectionResponse[];
          const section = sections.find(s => s.id === sectionId);
          if (section) {
            return section.steps.find(s => s.id === stepId);
          }
        }
        return null;
      }),
    );
  };

  onReset() {
    this.courseRepo.reset();
  }

  fetchCourse = (id: UserCourseId) => {
    return UserCourseAPI.fetchCourse(id).pipe(
      this.courseRepo.ops.upsertOne(item => ({ item })),
      tapError(() => {
        window.location.href = '/courses?trashed=true';
      }),
      //   handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  fetchCourseSessionId = () => {
    return UserCourseAPI.fetchCourseSessionId();
  };

  createStepTracker = (stepId: UserCourseStepId, sessionId: CourseSessionId, time: number) => {
    return UserCourseAPI.createStepTracker(stepId, sessionId, time);
  };

  stepComplete = (id: UserCourseId, stepId: UserCourseStepId, answers: CourseStepAnswers) => {
    return UserCourseAPI.stepComplete(id, stepId, answers).pipe(
      this.ops.updateStep(),
      handleMessage({ error: requestMessage('fetch_templates_error') }),
    );
  };

  onProgress = (stepId: string, progress: CourseStepAnswers) => {
    return UserCourseAPI.onProgress(stepId, progress).pipe(handleMessage({ error: requestMessage('fetch_templates_error') }));
  };
  submitEssay = (stepId: string, progress: CourseStepAnswers) => {
    return UserCourseAPI.submitEssay(stepId, progress).pipe(handleMessage({ error: requestMessage('fetch_templates_error') }));
  };

  updateStep = () => {
    return (source$: Observable<UserCourseStepResponse>): Observable<UserCourseStepResponse> => {
      return of(null).pipe(
        switchMap(() => source$),
        this.courseRepo.ops.updateOne(newStep => ({
          id: newStep.courseId,
          changes: course => {
            const newSections = course.sections.map(section => {
              if (section.id === newStep.sectionId) {
                const newSteps = section.steps.map(step => {
                  if (step.id === newStep.id) {
                    return newStep;
                  }
                  return step;
                });
                return { ...section, steps: newSteps };
              }
              return section;
            });
            return { sections: newSections };
          },
        })),
      );
    };
  };

  ops = { updateStep: this.updateStep };
}
